import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useHostingStore, HostedServer, HostedServerStatus, HostedServerBackup, HostedServerSubuser, ServerStats, HostingPermissions } from '../../store/hostingStore';
import { userAPI, pluginAPI } from '../../lib/api';
import {
  ArrowLeft, Play, Square, RotateCw, Skull, Terminal,
  Settings, Database, Users, Loader2, Send, HardDrive, Cpu, MemoryStick,
  Trash2, Plus, Clock, AlertTriangle, Wifi, WifiOff,
  Save, X, Search, Check, UserPlus, Shield, Puzzle, Download, Package,
  FolderOpen,
} from 'lucide-react';
import FilesTab from './FilesTab';

// ── ANSI → React spans ──────────────────────────────────────────────────────

const ANSI_COLORS: Record<number, string> = {
  30: '#4f4f4f', 31: '#ff5555', 32: '#50fa7b', 33: '#f1fa8c',
  34: '#6272a4', 35: '#ff79c6', 36: '#8be9fd', 37: '#f8f8f2',
  90: '#6272a4', 91: '#ff6e6e', 92: '#69ff94', 93: '#ffffa5',
  94: '#d6acff', 95: '#ff92df', 96: '#a4ffff', 97: '#ffffff',
};

const ANSI_BG_COLORS: Record<number, string> = {
  40: '#4f4f4f', 41: '#ff5555', 42: '#50fa7b', 43: '#f1fa8c',
  44: '#6272a4', 45: '#ff79c6', 46: '#8be9fd', 47: '#f8f8f2',
  100: '#6272a4', 101: '#ff6e6e', 102: '#69ff94', 103: '#ffffa5',
  104: '#d6acff', 105: '#ff92df', 106: '#a4ffff', 107: '#ffffff',
};

function parseAnsi(line: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let color: string | undefined;
  let bgColor: string | undefined;
  let bold = false;
  let italic = false;
  let underline = false;
  let idx = 0;

  // Split on ANSI escape sequences
  const regex = /\x1B\[([0-9;]*)m/g;
  let match: RegExpExecArray | null;
  let lastIndex = 0;

  while ((match = regex.exec(line)) !== null) {
    // Push text before this escape
    if (match.index > lastIndex) {
      const text = line.slice(lastIndex, match.index);
      if (text) {
        const style: React.CSSProperties = {};
        if (color) style.color = color;
        if (bgColor) style.backgroundColor = bgColor;
        if (bold) style.fontWeight = 'bold';
        if (italic) style.fontStyle = 'italic';
        if (underline) style.textDecoration = 'underline';
        parts.push(
          Object.keys(style).length > 0
            ? <span key={idx++} style={style}>{text}</span>
            : text,
        );
      }
    }
    lastIndex = regex.lastIndex;

    // Parse SGR codes
    const codes = match[1] ? match[1].split(';').map(Number) : [0];
    for (const code of codes) {
      if (code === 0) { color = undefined; bgColor = undefined; bold = false; italic = false; underline = false; }
      else if (code === 1) bold = true;
      else if (code === 3) italic = true;
      else if (code === 4) underline = true;
      else if (code === 22) bold = false;
      else if (code === 23) italic = false;
      else if (code === 24) underline = false;
      else if (code === 39) color = undefined;
      else if (code === 49) bgColor = undefined;
      else if (ANSI_COLORS[code]) color = ANSI_COLORS[code];
      else if (ANSI_BG_COLORS[code]) bgColor = ANSI_BG_COLORS[code];
    }
  }

  // Remaining text after last escape
  if (lastIndex < line.length) {
    const text = line.slice(lastIndex);
    if (text) {
      const style: React.CSSProperties = {};
      if (color) style.color = color;
      if (bgColor) style.backgroundColor = bgColor;
      if (bold) style.fontWeight = 'bold';
      if (italic) style.fontStyle = 'italic';
      if (underline) style.textDecoration = 'underline';
      parts.push(
        Object.keys(style).length > 0
          ? <span key={idx++} style={style}>{text}</span>
          : text,
      );
    }
  }

  return parts.length > 0 ? parts : [line];
}

const AnsiLine: React.FC<{ line: string }> = React.memo(({ line }) => {
  const parsed = useMemo(() => parseAnsi(line), [line]);
  return (
    <div className="text-gray-300 whitespace-pre-wrap break-all">
      {parsed}
    </div>
  );
});

interface Props {
  serverId: string;
  onBack: () => void;
}

type Tab = 'console' | 'files' | 'backups' | 'plugins' | 'settings' | 'subusers';

const STATUS_COLORS: Record<HostedServerStatus, string> = {
  online:     'text-emerald-400',
  starting:   'text-yellow-400',
  stopping:   'text-orange-400',
  offline:    'text-text-muted',
  installing: 'text-blue-400',
  suspended:  'text-red-400',
  hibernated: 'text-purple-400',
};

const STATUS_LABELS: Record<HostedServerStatus, string> = {
  online:     'En ligne',
  starting:   'Démarrage...',
  stopping:   'Arrêt...',
  offline:    'Hors ligne',
  installing: 'Installation...',
  suspended:  'Suspendu',
  hibernated: 'Hiberné',
};

const ServerManagement: React.FC<Props> = ({ serverId, onBack }) => {
  const {
    currentServer, stats, consoleLogs, isConsoleConnected, backups,
    fetchServer, sendPower, connectConsole, disconnectConsole, sendConsoleCommand,
    fetchBackups, createBackup, deleteBackup,
  } = useHostingStore();

  const [tab, setTab] = useState<Tab>('console');
  const [commandInput, setCommandInput] = useState('');
  const consoleEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchServer(serverId);
    fetchBackups(serverId);
  }, [serverId]);

  // Connect console once when component mounts (not per-tab), disconnect on unmount
  useEffect(() => {
    if (currentServer?.pterodactyl_identifier) {
      connectConsole(serverId);
      return () => disconnectConsole(serverId);
    }
  }, [currentServer?.pterodactyl_identifier, serverId]);

  // Auto-scroll console
  useEffect(() => {
    consoleEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [consoleLogs]);

  const server = currentServer;
  if (!server) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  const handleCommand = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commandInput.trim()) return;
    sendConsoleCommand(serverId, commandInput.trim());
    setCommandInput('');
  };

  const canPower = !['installing', 'suspended'].includes(server.status);

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'console',  label: 'Console',  icon: <Terminal className="w-4 h-4" /> },
    { id: 'files',    label: 'Fichiers', icon: <FolderOpen className="w-4 h-4" /> },
    { id: 'backups',  label: 'Backups',  icon: <Database className="w-4 h-4" /> },
    { id: 'plugins',  label: 'Plugins',  icon: <Puzzle className="w-4 h-4" /> },
    { id: 'settings', label: 'Paramètres', icon: <Settings className="w-4 h-4" /> },
    { id: 'subusers', label: 'Accès',    icon: <Users className="w-4 h-4" /> },
  ];

  return (
    <div className="h-full flex flex-col overflow-hidden bg-bg-primary">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 border-b border-border-color bg-bg-secondary/50 backdrop-blur-sm">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg hover:bg-bg-tertiary text-text-muted hover:text-text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-sm font-bold text-text-primary">{server.name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] font-medium ${STATUS_COLORS[server.status]}`}>
                  {STATUS_LABELS[server.status]}
                </span>
                <span className="text-[10px] text-text-muted">
                  {server.server_type} · {server.mc_version}
                </span>
                {server.subdomain && (
                  <span className="text-[10px] text-text-muted font-mono">
                    {server.subdomain}.mc.erozion.net
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Power buttons */}
          <div className="flex items-center gap-1.5">
            {(server.status === 'offline' || server.status === 'hibernated') && canPower && (
              <button
                onClick={() => sendPower(serverId, 'start')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/25 transition-colors"
              >
                <Play className="w-3.5 h-3.5" />
                Démarrer
              </button>
            )}
            {server.status === 'online' && (
              <>
                <button
                  onClick={() => sendPower(serverId, 'restart')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-yellow-500/15 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/25 transition-colors"
                >
                  <RotateCw className="w-3.5 h-3.5" />
                  Redémarrer
                </button>
                <button
                  onClick={() => sendPower(serverId, 'stop')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 transition-colors"
                >
                  <Square className="w-3.5 h-3.5" />
                  Arrêter
                </button>
              </>
            )}
            {(server.status === 'starting' || server.status === 'stopping') && (
              <button
                onClick={() => sendPower(serverId, 'kill')}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 transition-colors"
                title="Forcer l'arrêt"
              >
                <Skull className="w-3.5 h-3.5" />
                Kill
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-cyan-500 text-cyan-400'
                  : 'border-transparent text-text-muted hover:text-text-secondary'
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ─────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden">
        {tab === 'console' && (
          <ConsoleTab
            logs={consoleLogs}
            isConnected={isConsoleConnected}
            commandInput={commandInput}
            setCommandInput={setCommandInput}
            onSubmit={handleCommand}
            consoleEndRef={consoleEndRef}
            stats={stats}
            server={server}
          />
        )}
        {tab === 'backups' && (
          <BackupsTab
            backups={backups}
            serverId={serverId}
            onCreateBackup={() => createBackup(serverId)}
            onDeleteBackup={(backupId) => deleteBackup(serverId, backupId)}
          />
        )}
        {tab === 'files' && <FilesTab server={server} serverId={serverId} />}
        {tab === 'plugins' && <PluginsTab server={server} serverId={serverId} />}
        {tab === 'settings' && <SettingsTab server={server} serverId={serverId} onBack={onBack} />}
        {tab === 'subusers' && <SubusersTab server={server} serverId={serverId} />}
      </div>
    </div>
  );
};

// ── Console Tab ──────────────────────────────────────────────────────────────

const ConsoleTab: React.FC<{
  logs: string[];
  isConnected: boolean;
  commandInput: string;
  setCommandInput: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  consoleEndRef: React.RefObject<HTMLDivElement>;
  stats: ServerStats | null;
  server: HostedServer;
}> = ({ logs, isConnected, commandInput, setCommandInput, onSubmit, consoleEndRef, stats, server }) => {
  return (
    <div className="h-full flex flex-col">
      {/* Mini stats bar */}
      {stats && (
        <div className="flex-shrink-0 px-4 py-2 bg-bg-secondary/50 border-b border-border-color flex items-center gap-4 text-[10px] text-text-muted">
          <span className="flex items-center gap-1">
            <Cpu className="w-3 h-3" />
            CPU: <span className="text-text-primary font-medium">{stats.cpuAbsolute?.toFixed(1) ?? '—'}%</span>
          </span>
          <span className="flex items-center gap-1">
            <MemoryStick className="w-3 h-3" />
            RAM: <span className="text-text-primary font-medium">
              {stats.memoryBytes ? `${(stats.memoryBytes / 1024 / 1024).toFixed(0)} MB` : '—'}
            </span>
            <span className="text-text-muted/50">/ {server.memory_mb} MB</span>
          </span>
          <span className="flex items-center gap-1">
            <HardDrive className="w-3 h-3" />
            Disque: <span className="text-text-primary font-medium">
              {stats.diskBytes ? `${(stats.diskBytes / 1024 / 1024).toFixed(0)} MB` : '—'}
            </span>
          </span>
          <span className={`ml-auto flex items-center gap-1 ${isConnected ? 'text-emerald-400' : 'text-text-muted'}`}>
            {isConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {isConnected ? 'Connecté' : 'Déconnecté'}
          </span>
        </div>
      )}

      {/* Console output */}
      <div className="flex-1 overflow-y-auto bg-[#0d1117] font-mono text-[11px] leading-relaxed p-4">
        {logs.length === 0 ? (
          <div className="text-gray-500 italic">En attente de la connexion à la console...</div>
        ) : (
          logs.map((line, i) => (
            <AnsiLine key={i} line={line} />
          ))
        )}
        <div ref={consoleEndRef} />
      </div>

      {/* Command input */}
      <form onSubmit={onSubmit} className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-[#0d1117] border-t border-gray-800">
        <span className="text-cyan-400 text-xs font-mono">&gt;</span>
        <input
          type="text"
          value={commandInput}
          onChange={(e) => setCommandInput(e.target.value)}
          placeholder={isConnected ? 'Entrez une commande...' : 'Console non connectée'}
          disabled={!isConnected}
          className="flex-1 bg-transparent text-gray-200 text-xs font-mono outline-none placeholder:text-gray-600 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!isConnected || !commandInput.trim()}
          className="p-1.5 rounded-md text-cyan-400 hover:bg-cyan-500/10 disabled:opacity-30 transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};


// ── Backups Tab ──────────────────────────────────────────────────────────────

const BackupsTab: React.FC<{
  backups: HostedServerBackup[];
  serverId: string;
  onCreateBackup: () => void;
  onDeleteBackup: (backupId: string) => void;
}> = ({ backups, onCreateBackup, onDeleteBackup }) => {
  const visibleBackups = backups.filter((b) => b.status !== 'deleted');

  return (
    <div className="h-full overflow-y-auto gaming-scrollbar p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold text-text-primary">Backups</h2>
          <button
            onClick={onCreateBackup}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/25 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Créer un backup
          </button>
        </div>

        {visibleBackups.length === 0 ? (
          <div className="text-center py-12 text-text-muted text-sm">
            <Database className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Aucun backup
          </div>
        ) : (
          <div className="space-y-2">
            {visibleBackups.map((backup) => (
              <div
                key={backup.id}
                className="flex items-center justify-between p-3 bg-bg-secondary border border-border-color rounded-lg"
              >
                <div>
                  <div className="text-sm font-medium text-text-primary">{backup.name}</div>
                  <div className="flex items-center gap-2 text-[10px] text-text-muted mt-0.5">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(backup.created_at).toLocaleString('fr-FR')}
                    </span>
                    {backup.size_bytes > 0 && (
                      <span>{(backup.size_bytes / 1024 / 1024).toFixed(1)} MB</span>
                    )}
                    {backup.status === 'creating' && (
                      <span className="flex items-center gap-1 text-blue-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        En cours...
                      </span>
                    )}
                    {backup.status === 'failed' && (
                      <span className="flex items-center gap-1 text-red-400">
                        <AlertTriangle className="w-3 h-3" />
                        Échoué
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {backup.status === 'completed' && (
                    <button
                      onClick={() => onDeleteBackup(backup.id)}
                      className="p-1.5 rounded-md text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Plugins Tab ──────────────────────────────────────────────────────────────

interface ModrinthHit {
  slug: string;
  project_id: string;
  title: string;
  description: string;
  icon_url: string | null;
  downloads: number;
  follows: number;
  categories: string[];
  project_type: string;
  author: string;
}

interface InstalledFile {
  name: string;
  size: number;
  modifiedAt: string;
}

const PluginsTab: React.FC<{ server: HostedServer; serverId: string }> = ({ server, serverId }) => {
  const [view, setView] = useState<'browse' | 'installed'>('browse');
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<ModrinthHit[]>([]);
  const [totalHits, setTotalHits] = useState(0);
  const [searching, setSearching] = useState(false);
  const [installed, setInstalled] = useState<InstalledFile[]>([]);
  const [installedFolder, setInstalledFolder] = useState('plugins');
  const [loadingInstalled, setLoadingInstalled] = useState(false);
  const [installingId, setInstallingId] = useState<string | null>(null);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);

  const isModded = ['forge', 'fabric'].includes(server.server_type);
  const label = isModded ? 'Mods' : 'Plugins';

  // Search Modrinth
  useEffect(() => {
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await pluginAPI.search(serverId, searchQuery, 0, 30);
        setResults(data.hits);
        setTotalHits(data.total_hits);
      } catch {
        setResults([]);
      }
      setSearching(false);
    }, searchQuery ? 500 : 0);
    return () => clearTimeout(timeout);
  }, [searchQuery, serverId]);

  // Load installed
  const loadInstalled = async () => {
    setLoadingInstalled(true);
    try {
      const data = await pluginAPI.getInstalled(serverId);
      setInstalled(data.files);
      setInstalledFolder(data.folder);
    } catch {
      setInstalled([]);
    }
    setLoadingInstalled(false);
  };

  useEffect(() => {
    if (view === 'installed') loadInstalled();
  }, [view, serverId]);

  const handleInstall = async (projectId: string) => {
    setInstallingId(projectId);
    try {
      // Get the latest compatible version
      const versions = await pluginAPI.getVersions(serverId, projectId);
      if (versions.length === 0) {
        setInstallingId(null);
        return;
      }
      await pluginAPI.install(serverId, versions[0].id);
      // Refresh installed list
      loadInstalled();
    } catch {
      // error
    }
    setInstallingId(null);
  };

  const handleDelete = async (filename: string) => {
    setDeletingFile(filename);
    try {
      await pluginAPI.deleteInstalled(serverId, filename);
      setInstalled((prev) => prev.filter((f) => f.name !== filename));
    } catch {
      // error
    }
    setDeletingFile(null);
  };

  const formatDownloads = (n: number) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
    return String(n);
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header with view toggle */}
      <div className="flex-shrink-0 px-6 py-3 border-b border-border-color flex items-center gap-4">
        <div className="flex bg-bg-tertiary rounded-lg p-0.5">
          <button
            onClick={() => setView('browse')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              view === 'browse' ? 'bg-bg-secondary text-text-primary' : 'text-text-muted'
            }`}
          >
            Parcourir
          </button>
          <button
            onClick={() => setView('installed')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              view === 'installed' ? 'bg-bg-secondary text-text-primary' : 'text-text-muted'
            }`}
          >
            Installés ({installed.length})
          </button>
        </div>
        {view === 'browse' && (
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Rechercher des ${label.toLowerCase()} sur Modrinth...`}
              className="w-full bg-bg-secondary border border-border-color rounded-lg pl-9 pr-3 py-1.5 text-sm text-text-primary outline-none focus:border-cyan-500/50 transition-colors"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted animate-spin" />}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto gaming-scrollbar p-6">
        {view === 'browse' ? (
          <div className="max-w-4xl mx-auto">
            {results.length === 0 && !searching ? (
              <div className="text-center py-12 text-text-muted text-sm">
                <Puzzle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                {searchQuery ? `Aucun ${label.toLowerCase()} trouvé` : `Recherchez des ${label.toLowerCase()} compatibles avec ${server.server_type} ${server.mc_version}`}
              </div>
            ) : (
              <>
                {totalHits > 0 && (
                  <p className="text-[10px] text-text-muted mb-3">{totalHits} résultat{totalHits > 1 ? 's' : ''}</p>
                )}
                <div className="space-y-2">
                  {results.map((hit) => (
                    <div
                      key={hit.project_id}
                      className="flex items-start gap-3 p-3 bg-bg-secondary border border-border-color rounded-lg hover:border-border-color/80 transition-colors"
                    >
                      {hit.icon_url ? (
                        <img src={hit.icon_url} alt="" className="w-10 h-10 rounded-lg flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-bg-tertiary flex items-center justify-center flex-shrink-0">
                          <Package className="w-5 h-5 text-text-muted" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-text-primary truncate">{hit.title}</span>
                          <span className="text-[10px] text-text-muted">par {hit.author}</span>
                        </div>
                        <p className="text-xs text-text-muted line-clamp-2 mt-0.5">{hit.description}</p>
                        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-text-muted">
                          <span className="flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            {formatDownloads(hit.downloads)}
                          </span>
                          {hit.categories.slice(0, 3).map((cat) => (
                            <span key={cat} className="px-1.5 py-0.5 rounded bg-bg-tertiary">{cat}</span>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => handleInstall(hit.project_id)}
                        disabled={installingId === hit.project_id}
                        className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/25 disabled:opacity-50 transition-colors"
                      >
                        {installingId === hit.project_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Download className="w-3.5 h-3.5" />
                        )}
                        Installer
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto">
            {loadingInstalled ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
              </div>
            ) : installed.length === 0 ? (
              <div className="text-center py-12 text-text-muted text-sm">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Aucun {label.toLowerCase()} installé dans <code className="text-xs bg-bg-tertiary px-1 py-0.5 rounded">{installedFolder}/</code>
              </div>
            ) : (
              <div className="space-y-2">
                {installed.map((file) => (
                  <div
                    key={file.name}
                    className="flex items-center justify-between p-3 bg-bg-secondary border border-border-color rounded-lg"
                  >
                    <div>
                      <div className="text-sm font-medium text-text-primary font-mono">{file.name}</div>
                      <div className="flex items-center gap-2 text-[10px] text-text-muted mt-0.5">
                        <span>{(file.size / 1024 / 1024).toFixed(1)} MB</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(file.name)}
                      disabled={deletingFile === file.name}
                      className="p-1.5 rounded-md text-text-muted hover:text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors"
                      title="Supprimer"
                    >
                      {deletingFile === file.name ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Settings Tab ─────────────────────────────────────────────────────────────

const SettingsTab: React.FC<{ server: HostedServer; serverId: string; onBack: () => void }> = ({ server, serverId, onBack }) => {
  const { updateServer, deleteServer } = useHostingStore();
  const [name, setName] = useState(server.name);
  const [description, setDescription] = useState(server.description || '');
  const [maxPlayers, setMaxPlayers] = useState(server.max_players);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const hasChanges = name !== server.name || description !== (server.description || '') || maxPlayers !== server.max_players;

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await updateServer(serverId, {
      name: name.trim(),
      description: description.trim() || undefined,
      maxPlayers,
    });
    setSaving(false);
  };

  const handleDelete = async () => {
    setDeleting(true);
    await deleteServer(serverId);
    setDeleting(false);
    onBack();
  };

  return (
    <div className="h-full overflow-y-auto gaming-scrollbar p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <h2 className="text-sm font-bold text-text-primary">Paramètres du serveur</h2>

        {/* Editable fields */}
        <div className="space-y-4">
          <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
            <label className="text-xs text-text-muted block mb-1.5">Nom du serveur</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              className="w-full bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan-500/50 transition-colors"
            />
          </div>

          <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
            <label className="text-xs text-text-muted block mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={3}
              className="w-full bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan-500/50 transition-colors resize-none"
              placeholder="Description du serveur (optionnel)"
            />
          </div>

          <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
            <label className="text-xs text-text-muted block mb-1.5">Joueurs maximum</label>
            <input
              type="number"
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
              min={1}
              max={100}
              className="w-24 bg-bg-tertiary border border-border-color rounded-lg px-3 py-2 text-sm text-text-primary outline-none focus:border-cyan-500/50 transition-colors"
            />
          </div>
        </div>

        {/* Save button */}
        {hasChanges && (
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/25 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Sauvegarder
          </button>
        )}

        {/* Read-only info */}
        <div className="space-y-4 pt-2 border-t border-border-color">
          <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
            <label className="text-xs text-text-muted block mb-1.5">Type</label>
            <div className="text-sm text-text-primary capitalize">{server.server_type} · {server.mc_version}</div>
          </div>
          <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
            <label className="text-xs text-text-muted block mb-1.5">Adresse</label>
            <div className="text-sm text-text-primary font-mono">
              {server.subdomain ? `${server.subdomain}.mc.erozion.net` : `${server.allocation_ip || '—'}:${server.allocation_port || '—'}`}
            </div>
          </div>
          <div className="bg-bg-secondary border border-border-color rounded-xl p-4">
            <label className="text-xs text-text-muted block mb-1.5">Ressources</label>
            <div className="text-sm text-text-primary">
              {server.memory_mb} MB RAM · {(server.disk_mb / 1024).toFixed(0)} GB Disque · {server.cpu_limit}% CPU
            </div>
          </div>
        </div>

        {/* Danger zone */}
        <div className="pt-4 border-t border-red-500/20">
          <h3 className="text-xs font-bold text-red-400 mb-3">Zone dangereuse</h3>
          {!confirmDelete ? (
            <button
              onClick={() => setConfirmDelete(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer le serveur
            </button>
          ) : (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <p className="text-xs text-red-300 mb-3">
                Cette action est irréversible. Le serveur, ses backups et ses données seront définitivement supprimés.
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 transition-colors"
                >
                  {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  Confirmer la suppression
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-4 py-2 text-xs font-medium rounded-lg text-text-muted hover:text-text-primary transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Sub-users Tab ────────────────────────────────────────────────────────────

const PERMISSION_DEFS = [
  { bit: HostingPermissions.CONSOLE,  label: 'Console',        desc: 'Voir la console et envoyer des commandes' },
  { bit: HostingPermissions.POWER,    label: 'Power',          desc: 'Démarrer, arrêter, redémarrer le serveur' },
  { bit: HostingPermissions.BACKUPS,  label: 'Backups',        desc: 'Créer, voir et supprimer des backups' },
  { bit: HostingPermissions.SETTINGS, label: 'Paramètres',     desc: 'Modifier le nom, la description et les joueurs max' },
  { bit: HostingPermissions.SUBUSERS, label: 'Sous-utilisateurs', desc: 'Gérer les accès des autres utilisateurs' },
  { bit: HostingPermissions.PLUGINS,  label: 'Plugins',        desc: 'Installer et gérer les plugins/mods' },
  { bit: HostingPermissions.FILES,    label: 'Fichiers',       desc: 'Parcourir, modifier et uploader des fichiers' },
];

const SubusersTab: React.FC<{ server: HostedServer; serverId: string }> = ({ server, serverId }) => {
  const { fetchSubusers, addSubuser, updateSubuserPermissions, removeSubuser } = useHostingStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ id: string; username: string; avatar_url: string | null }[]>([]);
  const [searching, setSearching] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPerms, setEditPerms] = useState(0);

  useEffect(() => {
    fetchSubusers(serverId);
  }, [serverId]);

  // Debounced user search
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await userAPI.findUserByUsername(searchQuery);
        if (result && !result.id?.startsWith('temp-')) {
          // Filter out existing subusers and the owner
          const existingIds = new Set([server.owner_id, ...(server.subusers?.map(s => s.user_id) || [])]);
          const results = Array.isArray(result) ? result : [result];
          setSearchResults(results.filter((u: any) => !existingIds.has(u.id)));
        } else {
          setSearchResults([]);
        }
      } catch {
        setSearchResults([]);
      }
      setSearching(false);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchQuery, server.owner_id, server.subusers]);

  const handleAdd = async (userId: string) => {
    setAdding(true);
    const defaultPerms = HostingPermissions.CONSOLE | HostingPermissions.POWER;
    const ok = await addSubuser(serverId, userId, defaultPerms);
    if (ok) {
      setSearchQuery('');
      setSearchResults([]);
    }
    setAdding(false);
  };

  const handleStartEdit = (su: HostedServerSubuser) => {
    setEditingId(su.id);
    setEditPerms(su.permissions);
  };

  const handleSavePerms = async (subuserId: string) => {
    await updateSubuserPermissions(serverId, subuserId, editPerms);
    setEditingId(null);
  };

  const togglePerm = (bit: number) => {
    setEditPerms((prev) => prev ^ bit);
  };

  return (
    <div className="h-full overflow-y-auto gaming-scrollbar p-6">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-sm font-bold text-text-primary mb-4">Gestion des accès</h2>

        {/* Add sub-user search */}
        <div className="mb-6">
          <label className="text-xs text-text-muted block mb-1.5">Ajouter un utilisateur</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par nom d'utilisateur..."
              className="w-full bg-bg-secondary border border-border-color rounded-lg pl-9 pr-3 py-2 text-sm text-text-primary outline-none focus:border-cyan-500/50 transition-colors"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted animate-spin" />}
          </div>

          {/* Search results dropdown */}
          {searchResults.length > 0 && (
            <div className="mt-1 bg-bg-secondary border border-border-color rounded-lg overflow-hidden">
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleAdd(user.id)}
                  disabled={adding}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-bg-tertiary transition-colors text-left disabled:opacity-50"
                >
                  {user.avatar_url ? (
                    <img src={user.avatar_url} className="w-7 h-7 rounded-full" alt="" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-bg-tertiary flex items-center justify-center text-text-muted text-xs font-bold">
                      {user.username[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm text-text-primary flex-1">{user.username}</span>
                  <UserPlus className="w-3.5 h-3.5 text-cyan-400" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Existing sub-users */}
        {server.subusers && server.subusers.length > 0 ? (
          <div className="space-y-2">
            {server.subusers.map((su) => (
              <div
                key={su.id}
                className="bg-bg-secondary border border-border-color rounded-lg overflow-hidden"
              >
                <div className="flex items-center gap-3 p-3">
                  {su.avatar_url ? (
                    <img src={su.avatar_url} className="w-8 h-8 rounded-full" alt="" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-bg-tertiary flex items-center justify-center text-text-muted text-xs font-bold">
                      {su.username[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary">{su.username}</div>
                    <div className="text-[10px] text-text-muted">
                      {PERMISSION_DEFS.filter(p => (su.permissions & p.bit) !== 0).map(p => p.label).join(', ') || 'Aucune permission'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {editingId === su.id ? (
                      <>
                        <button
                          onClick={() => handleSavePerms(su.id)}
                          className="p-1.5 rounded-md text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                          title="Sauvegarder"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="p-1.5 rounded-md text-text-muted hover:text-text-primary transition-colors"
                          title="Annuler"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleStartEdit(su)}
                          className="p-1.5 rounded-md text-text-muted hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors"
                          title="Modifier les permissions"
                        >
                          <Shield className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => removeSubuser(serverId, su.id)}
                          className="p-1.5 rounded-md text-text-muted hover:text-red-400 hover:bg-red-500/10 transition-colors"
                          title="Retirer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Permission editor */}
                {editingId === su.id && (
                  <div className="px-3 pb-3 pt-1 border-t border-border-color">
                    <div className="grid grid-cols-2 gap-2">
                      {PERMISSION_DEFS.map((perm) => (
                        <label
                          key={perm.bit}
                          className="flex items-start gap-2 p-2 rounded-lg hover:bg-bg-tertiary cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={(editPerms & perm.bit) !== 0}
                            onChange={() => togglePerm(perm.bit)}
                            className="mt-0.5 accent-cyan-500"
                          />
                          <div>
                            <div className="text-xs font-medium text-text-primary">{perm.label}</div>
                            <div className="text-[10px] text-text-muted">{perm.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-text-muted text-sm">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Aucun sous-utilisateur
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerManagement;
